/** Kennzahlen auf dem Workflow-Modell (Schritt 2). */
export interface DashboardKpis {
  neu_unbearbeitet: number;
  neu_gesamt: number;
  in_bearbeitung: number;
  kontaktversuch: number;
  gesprochen: number;
  qualifiziert: number;
  aktiv: number;
  talentpool: number;
  gesamt: number;
  je_stufe: Record<string, number>;
}

export interface TodayTasks {
  calls: TaskCandidate[];
  followups: TaskCandidate[];
  interviews_today: InterviewTask[];
  waiting_client_feedback: TaskCandidate[];
}

export interface TaskCandidate {
  id: string;
  name: string;
  position?: string;
  city?: string;
  stage: string;
  created_at?: string;
  last_contact_at?: string;
  next_follow_up_at?: string;
  client_last_response_at?: string;
  stage_updated_at?: string;
}

export interface InterviewTask {
  id: string;
  candidate_id: string;
  candidate_name: string;
  starts_at: string;
  ends_at?: string;
  location?: string;
  status: string;
}

export interface PipelineBoardCandidate {
  id: string;
  name: string;
  position?: string;
  city?: string;
  salary_expectation?: string;
  stage_updated_at: string;
  last_contact_at?: string;
  dnc: boolean;
  assigned_user_id?: string;
  assigned_name?: string;
  nba?: {
    action: string;
    reason: string;
    priority?: 'high' | 'medium' | 'low';
  };
}

export interface RecruiterLeaderboardEntry {
  user_id: string;
  name: string;
  avatar_url?: string;
  active_candidates: number;
  submissions: number;
  hires: number;
  interviews_scheduled: number;
}

export interface ActivityEntry {
  id: string;
  user_name?: string;
  entity_type: string;
  entity_id: string;
  entity_name?: string;
  action: string;
  metadata: Record<string, any>;
  created_at: string;
}

// Use relative path so it works in both dev and production
const API_BASE_URL = '/api/v1';

async function fetchDashboard<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`Dashboard API error: ${response.status}`);
  }

  const json = await response.json();
  return json.data;
}

// ---- Fokus-Dashboard (Overview) ----
export interface OverviewCandidate {
  id: string;
  name: string;
  position?: string | null;
  createdAt: string;
  stage?: string | null;       // führendes Statusfeld, siehe constants/candidateStage
  editedBy?: string | null;    // "Bearbeitet von" (Name oder "Recruiting Pool")
  assignedUser?: string | null;
  /** Nur „Im Interview": jüngste Bewegung aus interviews/submissions */
  lastMovementAt?: string | null;
  /** Nur „Im Interview": frühester offener Gesprächstermin */
  nextInterviewAt?: string | null;
}

export interface OverviewInterview {
  id: string;
  startsAt: string;
  candidateId?: string | null;
  candidateName?: string | null;
  clientCompanyId?: string | null;
  clientCompanyName?: string | null;
  status: string;
}

export interface OverviewCompany {
  id: string;
  name: string;
  createdAt: string;
}

export interface OverviewUserAppointment {
  id: string;
  title: string;
  startTime?: string | null;
  type?: string | null;
}

export interface OverviewUserAppointments {
  userId: string;
  userName: string;
  count: number;
  appointments: OverviewUserAppointment[];
}

export interface DashboardOverview {
  /** Die zuletzt angelegten Kandidaten (max. 20, neueste zuerst) */
  latestCandidates: OverviewCandidate[];
  /** Kandidaten mit offenem Interview bzw. Vorstellung im Interview-Status
   *  (aus interviews/submissions), zuletzt bewegte zuerst, max. 20 */
  inInterview: OverviewCandidate[];
  /** Gesamtzahl der Kandidaten in der Interviewphase (ungekürzt) */
  inInterviewTotal?: number;
  newCompaniesThisMonth: OverviewCompany[];
  appointmentsByUserToday: OverviewUserAppointments[];
}

const EMPTY_OVERVIEW: DashboardOverview = {
  latestCandidates: [],
  inInterview: [],
  newCompaniesThisMonth: [],
  appointmentsByUserToday: [],
};

const dashboardService = {
  async getOverview(): Promise<DashboardOverview> {
    const data = await fetchDashboard<DashboardOverview>('/dashboard/overview');
    return data || EMPTY_OVERVIEW;
  },

  async getKpis(): Promise<DashboardKpis> {
    return fetchDashboard<DashboardKpis>('/dashboard/kpis');
  },

  async getPipelineCounts(): Promise<Record<string, number>> {
    return fetchDashboard<Record<string, number>>('/dashboard/pipeline');
  },

  async getPipelineBoard(limit = 5): Promise<Record<string, PipelineBoardCandidate[]>> {
    return fetchDashboard<Record<string, PipelineBoardCandidate[]>>(`/dashboard/pipeline-board?limit=${limit}`);
  },

  async getTodayTasks(): Promise<TodayTasks> {
    const data = await fetchDashboard<TodayTasks>('/dashboard/today-tasks');
    return data || { calls: [], followups: [], interviews_today: [], waiting_client_feedback: [] };
  },

  async getRecentActivity(limit = 30): Promise<ActivityEntry[]> {
    const data = await fetchDashboard<ActivityEntry[]>(`/dashboard/activity?limit=${limit}`);
    return data || [];
  },

  async logActivity(payload: {
    entity_type: string;
    entity_id: string;
    entity_name?: string;
    action: string;
    metadata?: Record<string, any>;
  }): Promise<void> {
    await fetchDashboard('/dashboard/activity', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async changeStage(candidateId: string, stage: string): Promise<any> {
    return fetchDashboard(`/dashboard/candidate/${candidateId}/stage`, {
      method: 'PATCH',
      body: JSON.stringify({ stage }),
    });
  },

  async markContacted(candidateId: string): Promise<any> {
    return fetchDashboard(`/dashboard/candidate/${candidateId}/contacted`, {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  },

  async getRecruitingKpis(): Promise<DashboardKpis & {
    submissions_pending: number;
    submissions_month: number;
    interviews_this_week: number;
  }> {
    return fetchDashboard('/dashboard/kpis');
  },

  async getLeaderboard(): Promise<RecruiterLeaderboardEntry[]> {
    // This will be served by the recruiter_leaderboard() RPC
    // For now fallback to empty if endpoint not ready
    try {
      return await fetchDashboard<RecruiterLeaderboardEntry[]>('/dashboard/leaderboard');
    } catch {
      return [];
    }
  }
};

export default dashboardService;
